import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import {
  convertDateToStringFormat,
  getPrivateAdminDashboardMissionPath,
  getPrivateAdminDashboardPurchaseOrderPath,
} from '@bloomays-lib/utils.shared';
import { EditCraDaysManualInput, GqlActivitySignature, Maybe } from '@bloomays-lib/types.shared';
import { HrefLink } from '@bloomays-lib/ui.shared';
import { StatusChip, formatEuro } from '@bloomays-lib/ui.shared/datagrid';
import {
  FACT_CHIP,
  SIG_CHIP,
  CraDashboardRow,
  SignatureState,
  canForceCRA,
  canRelaunchBloomer,
  canRelaunchClient,
  canRetryBloomerInvoice,
  canRetryClientInvoice,
  canUploadAdditionalInvoice,
  canUploadBloomerInvoice,
  canEditCraDays,
  canVerifyCra,
  craAmountBreakdown,
  craSignatureTooltip,
  craStatusChip,
} from '../../helpers/craDashboard';
import type { FreelanceAmountParts } from '@bloomays-lib/utils.shared';
import { useAuthStore } from '../../stores';
import { Role } from '@bloomays-lib/types.shared';
import { useTranslation } from 'react-i18next';
import EditCraDaysForm from './EditCraDaysForm';

export type CRADetailPanelProps = {
  row: CraDashboardRow;
  onClose: () => void;
  /** Contextual actions wired by the page (status-gated). */
  onForceCRA?: (row: CraDashboardRow) => void;
  onRelaunchClient?: (row: CraDashboardRow) => void;
  onRelaunchBloomer?: (row: CraDashboardRow) => void;
  onRetryClientInvoice?: (row: CraDashboardRow) => void;
  onRetryBloomerInvoice?: (row: CraDashboardRow) => void;
  onUploadBloomerInvoice?: (row: CraDashboardRow) => void;
  onUploadAdditionalInvoice?: (row: CraDashboardRow) => void;
  craDaysEditOpen?: boolean;
  onOpenCraDaysEdit?: () => void;
  onCloseCraDaysEdit?: () => void;
  onSaveCraDaysEdit?: (input: EditCraDaysManualInput) => Promise<void>;
  savingCraDaysEdit?: boolean;
  onOpenCraDaysHistory?: () => void;
  /** Open a secured file preview (CRA / Bloomer invoice). */
  onPreview?: (url: string, title: string) => void;
  /** Rejoue le CRA du mois en lecture de contrôle (popin de vérification). */
  onVerifyCra?: (row: CraDashboardRow) => void;
};

const fmtMonth = (d?: Date | null) => (d ? convertDateToStringFormat(new Date(d), 'MM/yyyy') : '—');
const fmtDate = (d?: Date | null) => (d ? convertDateToStringFormat(new Date(d), 'dd/MM/yyyy') : '');

/** Signature pill, with a tooltip exposing the parsed signature details if any. */
const SignatureChip = ({ state, info }: { state: SignatureState; info: Maybe<GqlActivitySignature> | undefined }) => {
  const chip = (
    <StatusChip tone={SIG_CHIP[state].tone} dot>
      {SIG_CHIP[state].label}
    </StatusChip>
  );
  const tip = craSignatureTooltip(info);
  return tip ? (
    <Tooltip title={tip} arrow>
      <span>{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
};

/** Ligne d'un montant décomposé normal / HNO / total (— si le TJM est inconnu). */
const AmountRow = ({
  label,
  parts,
  strong,
}: {
  label: string;
  parts: FreelanceAmountParts | null;
  strong?: boolean;
}) => (
  <tr>
    <th>{label}</th>
    <td>{parts ? formatEuro(parts.normal) : '—'}</td>
    <td>{parts ? formatEuro(parts.hno) : '—'}</td>
    <td>{parts ? strong ? <strong>{formatEuro(parts.total)}</strong> : formatEuro(parts.total) : '—'}</td>
  </tr>
);

const CRADetailPanel = ({
  row,
  onClose,
  onForceCRA,
  onRelaunchClient,
  onRelaunchBloomer,
  onRetryClientInvoice,
  onRetryBloomerInvoice,
  onUploadBloomerInvoice,
  onUploadAdditionalInvoice,
  craDaysEditOpen = false,
  onOpenCraDaysEdit,
  onCloseCraDaysEdit,
  onSaveCraDaysEdit,
  savingCraDaysEdit = false,
  onOpenCraDaysHistory,
  onPreview,
  onVerifyCra,
}: CRADetailPanelProps) => {
  const { t } = useTranslation(['craDaysEdit', 'craVerification']);
  const auth = useAuthStore((state) => state.auth);
  const userRoles = auth?.user?.roles ?? [];
  const showEditCraDays = canEditCraDays(row, userRoles as Role[]);
  const cra = craStatusChip(row);
  const hasNps = row.clientScore !== null || row.bloomerScore !== null;

  // Montants estimés à partir du TJM (vente / achat, décomposés normal / HNO).
  const amounts = craAmountBreakdown(row);
  const margin: FreelanceAmountParts | null =
    amounts.sales && amounts.purchase
      ? {
          normal: amounts.sales.normal - amounts.purchase.normal,
          hno: amounts.sales.hno - amounts.purchase.hno,
          total: amounts.sales.total - amounts.purchase.total,
        }
      : null;
  const hasAmounts = !!amounts.sales || !!amounts.purchase;

  return (
    <Panel>
      <Head>
        <div>
          <Client>{row.client || '—'}</Client>
          <MissionLabel>{row.mission || '—'}</MissionLabel>
        </div>
        <IconButton aria-label="Fermer" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Head>

      <ChipRow>
        <StatusChip tone={cra.tone}>{cra.label}</StatusChip>
        <StatusChip tone="neutral">Mois {fmtMonth(row.activityMonth)}</StatusChip>
      </ChipRow>

      <Section>
        <SectionTitle>Références</SectionTitle>
        <Line>
          <Key>Mission</Key>
          <Val>
            <HrefLink
              href={getPrivateAdminDashboardMissionPath(row.missionId)}
              target="_self"
              color="tertiary"
              title="Voir la mission"
              icon={<OpenInNewIcon fontSize="small" />}
              text={row.missionId}
              iconPosition="right"
            />
          </Val>
        </Line>
        <Line>
          <Key>CRA (activité)</Key>
          <Val>
            <Mono>{row.activityId || '—'}</Mono>
          </Val>
        </Line>
        {row.orderNumber ? (
          <Line>
            <Key>Bon de commande</Key>
            <Val>
              <HrefLink
                href={getPrivateAdminDashboardPurchaseOrderPath(row.orderNumber)}
                target="_self"
                color="tertiary"
                title="Voir le BDC"
                icon={<OpenInNewIcon fontSize="small" />}
                text={row.orderNumber}
                iconPosition="right"
              />
            </Val>
          </Line>
        ) : null}
      </Section>

      <Section>
        <SectionTitle>Acteurs</SectionTitle>
        <Line>
          <Key>Bloomer</Key>
          <Val>{row.bloomer || '—'}</Val>
        </Line>
        <Line>
          <Key>Société Bloomer</Key>
          <Val>{row.bloomerSocietyName || '—'}</Val>
        </Line>
        <Line>
          <Key>Contact client</Key>
          <Val>{row.contactOperationsName || '—'}</Val>
        </Line>
        {row.accountManager ? (
          <Line>
            <Key>Account manager</Key>
            <Val>{row.accountManager}</Val>
          </Line>
        ) : null}
      </Section>

      <Section>
        <SectionTitle>CRA &amp; signatures</SectionTitle>

        {craDaysEditOpen && onSaveCraDaysEdit && onCloseCraDaysEdit ? (
          <EditCraDaysForm
            key={row.activityId}
            row={row}
            saving={savingCraDaysEdit}
            onCancel={onCloseCraDaysEdit}
            onSave={onSaveCraDaysEdit}
          />
        ) : (
          <>
            <Line>
              <Key>Jours saisis / prévus</Key>
              <Val>
                {row.daysEntered} / {row.daysPlanned || '—'}
              </Val>
            </Line>
            {showEditCraDays ? (
              <CraDaysEditLink type="button" onClick={() => onOpenCraDaysEdit?.()}>
                <EditOutlinedIcon fontSize="small" />
                {t('actionLabel', { ns: 'craDaysEdit' })}
              </CraDaysEditLink>
            ) : null}
            {row.daysManuallyModified ? (
              <CraDaysEditLink type="button" onClick={() => onOpenCraDaysHistory?.()}>
                <HistoryOutlinedIcon fontSize="small" />
                {t('viewHistoryLink', { ns: 'craDaysEdit' })}
              </CraDaysEditLink>
            ) : null}
            <Line>
              <Key>Signature Bloomer</Key>
              <Val>
                <SignatureChip state={row.bloomerSignature} info={row.bloomerSignatureInfo} />
              </Val>
            </Line>
            <Line>
              <Key>Signature client</Key>
              <Val>
                <SignatureChip state={row.clientSignature} info={row.clientSignatureInfo} />
              </Val>
            </Line>
            {row.craURL ? (
              <PreviewLink type="button" onClick={() => onPreview?.(row.craURL as string, `CRA — ${row.bloomer}`)}>
                <VisibilityOutlinedIcon fontSize="small" />
                Aperçu du CRA
              </PreviewLink>
            ) : null}
            {/* Contrôle du mois déclaré face à la facture fournisseur — indépendant du PDF signé. */}
            {onVerifyCra && canVerifyCra(row) ? (
              <PreviewLink type="button" onClick={() => onVerifyCra(row)}>
                <FactCheckOutlinedIcon fontSize="small" />
                {t('actionLabel', { ns: 'craVerification' })}
              </PreviewLink>
            ) : null}
            {row.invoiceURL ? (
              <PreviewLink
                type="button"
                onClick={() => onPreview?.(row.invoiceURL as string, `Facture Bloomer — ${row.bloomer}`)}
              >
                <VisibilityOutlinedIcon fontSize="small" />
                Aperçu de la facture Bloomer
              </PreviewLink>
            ) : null}
            {row.additionalInvoiceURL ? (
              <PreviewLink
                type="button"
                onClick={() =>
                  onPreview?.(row.additionalInvoiceURL as string, `Facture additionnelle — ${row.bloomer}`)
                }
              >
                <VisibilityOutlinedIcon fontSize="small" />
                Aperçu de la facture additionnelle
              </PreviewLink>
            ) : null}
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>Montants estimés (TJM)</SectionTitle>
        <Line>
          <Key>Jours ouvrés / HNO</Key>
          <Val>
            {amounts.normalDays} / {amounts.hnoDays}
          </Val>
        </Line>
        {hasAmounts ? (
          <AmountTable>
            <thead>
              <tr>
                <th />
                <th>Normal</th>
                <th>HNO</th>
                <th>Total HT</th>
              </tr>
            </thead>
            <tbody>
              <AmountRow label="Vente" parts={amounts.sales} />
              <AmountRow label="Achat" parts={amounts.purchase} />
              {margin ? <AmountRow label="Marge" parts={margin} strong /> : null}
            </tbody>
          </AmountTable>
        ) : (
          <Comment>Tarif (TJM) non disponible pour ce mois.</Comment>
        )}
      </Section>

      <Section>
        <SectionTitle>Facturation</SectionTitle>
        <Line>
          <Key>Facturation client</Key>
          <Val>
            <StatusChip tone={FACT_CHIP[row.clientFact].tone}>{FACT_CHIP[row.clientFact].label}</StatusChip>
            {row.clientAmountHT ? <Amount>{formatEuro(row.clientAmountHT)}</Amount> : null}
          </Val>
        </Line>
        {row.clientInvoiceLastError ? <ErrorText>⚠ {row.clientInvoiceLastError}</ErrorText> : null}
        {row.clientInvoicePennylaneUrl ? (
          <PennylaneLink href={row.clientInvoicePennylaneUrl} target="_blank" rel="noreferrer">
            <LaunchOutlinedIcon fontSize="small" />
            Ouvrir la facture client dans Pennylane
          </PennylaneLink>
        ) : null}
        {row.clientInvoiceMarkupPennylaneUrl ? (
          <PennylaneLink href={row.clientInvoiceMarkupPennylaneUrl} target="_blank" rel="noreferrer">
            <LaunchOutlinedIcon fontSize="small" />
            Ouvrir la facture client HNO dans Pennylane
          </PennylaneLink>
        ) : null}
        <Line>
          <Key>Facturation Bloomer</Key>
          <Val>
            <StatusChip tone={FACT_CHIP[row.bloomerFact].tone}>{FACT_CHIP[row.bloomerFact].label}</StatusChip>
            {row.bloomerAmountHT ? <Amount>{formatEuro(row.bloomerAmountHT)}</Amount> : null}
          </Val>
        </Line>
        {row.bloomerInvoiceLastError ? <ErrorText>⚠ {row.bloomerInvoiceLastError}</ErrorText> : null}
        {row.bloomerInvoicePennylaneUrl ? (
          <PennylaneLink href={row.bloomerInvoicePennylaneUrl} target="_blank" rel="noreferrer">
            <LaunchOutlinedIcon fontSize="small" />
            Ouvrir la facture Bloomer dans Pennylane
          </PennylaneLink>
        ) : null}
      </Section>

      {hasNps ? (
        <Section>
          <SectionTitle>Satisfaction (NPS)</SectionTitle>
          <Line>
            <Key>Note client</Key>
            <Val>{row.clientScore !== null && row.clientScore !== undefined ? `${row.clientScore}/10` : '—'}</Val>
          </Line>
          {row.clientComment ? (
            <Comment>
              « {row.clientComment} » {row.clientScoreDate ? `· ${fmtDate(row.clientScoreDate)}` : ''}
            </Comment>
          ) : null}
          <Line>
            <Key>Note Bloomer</Key>
            <Val>{row.bloomerScore !== null && row.bloomerScore !== undefined ? `${row.bloomerScore}/10` : '—'}</Val>
          </Line>
          {row.bloomerComment ? (
            <Comment>
              « {row.bloomerComment} » {row.bloomerScoreDate ? `· ${fmtDate(row.bloomerScoreDate)}` : ''}
            </Comment>
          ) : null}
        </Section>
      ) : null}

      <Actions>
        <ActionLink type="button" disabled={!canForceCRA(row)} onClick={() => onForceCRA?.(row)}>
          ⚡ Forcer le CRA
        </ActionLink>
        <ActionLink type="button" disabled={!canRelaunchClient(row)} onClick={() => onRelaunchClient?.(row)}>
          📧 Relancer la signature client
        </ActionLink>
        <ActionLink type="button" disabled={!canRelaunchBloomer(row)} onClick={() => onRelaunchBloomer?.(row)}>
          📧 Relancer la signature Bloomer
        </ActionLink>
        <ActionLink type="button" disabled={!canRetryClientInvoice(row)} onClick={() => onRetryClientInvoice?.(row)}>
          💳 Relancer la facturation client
        </ActionLink>
        <ActionLink type="button" disabled={!canRetryBloomerInvoice(row)} onClick={() => onRetryBloomerInvoice?.(row)}>
          💰 Relancer la facturation Bloomer
        </ActionLink>
        <ActionLink
          type="button"
          disabled={!canUploadBloomerInvoice(row, userRoles as Role[])}
          onClick={() => onUploadBloomerInvoice?.(row)}
        >
          📤 Uploader la facture Bloomer
        </ActionLink>
        <ActionLink
          type="button"
          disabled={!canUploadAdditionalInvoice(row, userRoles as Role[])}
          onClick={() => onUploadAdditionalInvoice?.(row)}
        >
          📎 Uploader la facture additionnelle
        </ActionLink>
      </Actions>

      <LinksRow>
        <HrefLink
          href={getPrivateAdminDashboardMissionPath(row.missionId)}
          target="_self"
          color="tertiary"
          icon={<OpenInNewIcon fontSize="small" />}
          text="Voir la mission"
        />
        <HrefLink
          href={`${getPrivateAdminDashboardMissionPath(row.missionId)}/activities`}
          target="_self"
          color="tertiary"
          icon={<OpenInNewIcon fontSize="small" />}
          text="Ouvrir les activités de la mission"
        />
      </LinksRow>
    </Panel>
  );
};

export default CRADetailPanel;
export { CRADetailPanel };

const Panel = styled('div')(() => ({
  width: 'min(420px, 92vw)',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: '20px 24px 28px',
}));

const Head = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
}));

const Client = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const MissionLabel = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
  marginTop: 2,
}));

const ChipRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
}));

const Section = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
}));

const SectionTitle = styled('div')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const Line = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

const Key = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
}));

const Val = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[90],
}));

const CraDaysEditLink = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.blue[60],
  cursor: 'pointer',
  width: 'fit-content',
  '&:hover': { textDecoration: 'underline' },
}));

const Amount = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 700,
  color: theme.palette.neutral[70],
}));

const AmountTable = styled('table')(({ theme }) => ({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12.5,
  color: theme.palette.neutral[90],
  '& th, & td': { padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' },
  '& thead th': {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[50],
    borderBottom: `1px solid ${theme.palette.neutral[15]}`,
  },
  '& tbody th': { textAlign: 'left', fontWeight: 600, color: theme.palette.neutral[60], paddingLeft: 0 },
  '& tbody strong': { color: theme.palette.blue[60] },
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
}));

const ActionLink = styled('button')(({ theme }) => ({
  border: 'none',
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  padding: '6px 0',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.blue[60],
  '&:disabled': { color: theme.palette.neutral[40], cursor: 'not-allowed' },
}));

const LinksRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
}));

const Mono = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.neutral[70],
}));

const PennylaneLink = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12.5,
  fontWeight: 600,
  color: theme.palette.blue[60],
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
}));

const PreviewLink = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.blue[60],
  '&:hover': { textDecoration: 'underline' },
}));

const Comment = styled('div')(({ theme }) => ({
  fontSize: 12,
  fontStyle: 'italic',
  color: theme.palette.neutral[60],
}));

const ErrorText = styled('div')(() => ({
  fontSize: 12,
  fontWeight: 600,
  color: '#B90A00',
  wordBreak: 'break-word',
}));
